/**
 * Device-class detection (desktop / tablet / mobile) for the video allocation
 * caps in src/config/qualityPresets.config.js (DEVICE_CLASS_CAPS). Distinct
 * from src/utils/mobileUtils.js, whose binary UA check answers a different
 * question ("force touch controls?"): modern iPadOS reports a Macintosh UA, so
 * a UA regex alone can never reach the tablet tier - the tell is a Mac UA
 * with a multi-point touchscreen (real Macs report maxTouchPoints 0).
 *
 * Touchscreen Windows laptops stay 'desktop' on purpose: they have desktop
 * GPUs and screens, and the caps are a rendering budget, not an input mode.
 *
 * Pure: the environment is injectable for unit tests.
 */

export type DeviceClass = 'desktop' | 'tablet' | 'mobile';

export interface DeviceClassEnv {
  /** defaults to navigator.userAgent */
  ua?: string;
  /** defaults to navigator.maxTouchPoints */
  maxTouchPoints?: number;
  /** screen's short side in CSS px; defaults to min(screen.width, screen.height) */
  shortSide?: number;
}

/** Classify the current (or an injected) environment. */
export function detectDeviceClass(env: DeviceClassEnv = {}): DeviceClass {
  const ua = env.ua ?? navigator.userAgent;
  const maxTouchPoints = env.maxTouchPoints ?? (navigator.maxTouchPoints || 0);
  const shortSide =
    env.shortSide ?? Math.min(window.screen?.width || 0, window.screen?.height || 0);

  // Phones: classic phone UA tokens, or Android WITH the "Mobile" token
  // (Android tablets omit it).
  if (/iPhone|iPod|Windows Phone|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
  if (/Android/i.test(ua) && /Mobile/i.test(ua)) return 'mobile';

  // Tablets: iPad (legacy UA token, or desktop-UA iPadOS = Mac UA + real
  // touchscreen) and Android without "Mobile". Small-screened ones behave
  // like phones for rendering purposes.
  const isIpad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1);
  const isAndroidTablet = /Android/i.test(ua);
  if (isIpad || isAndroidTablet) return shortSide >= 600 ? 'tablet' : 'mobile';

  return 'desktop';
}
